import React, { useContext } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import {
  faArrowRight,
  faLeaf,
  faQrcode,
  faReceipt
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ThemeContext } from '../components/ThemeContext.js'
import './HomeScreen.css'

const HomeScreen = () => {
  const { theme } = useContext(ThemeContext)

  return (
    <div
      className='home-screen'
      style={{
        '--home-bg': theme.bg,
        '--home-card': theme.card,
        '--home-text': theme.text,
        '--home-subtext': theme.subtext,
        '--home-accent': theme.accent,
        '--home-border': theme.sidebarAccent,
        '--home-muted': theme.surfaceMuted
      }}
    >
      <section className='home-hero'>
        <Row className='align-items-center gy-5'>
          <Col lg={6}>
            <div className='home-eyebrow'>
              <span className='home-eyebrow-dot' />A better way to keep receipts
            </div>
            <h1>
              Paper receipts are easy to lose.
              <span> Your digital ones aren’t.</span>
            </h1>
            <p className='home-hero-copy'>
              Keep your purchases together with MicroReceipt. Scan your personal
              QR code at checkout and your receipt is ready when you need it.
            </p>
            <div className='home-hero-actions'>
              <Button as={Link} to='/register' variant='primary' size='lg'>
                Get started <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button
                as={Link}
                to='/businessregister'
                variant='outline-primary'
                size='lg'
              >
                For businesses
              </Button>
            </div>
            <p className='home-signin'>
              Already have an account? <Link to='/login'>Sign in</Link>
            </p>
          </Col>

          <Col lg={6}>
            <div className='home-preview'>
              <img
                className='home-hero-image'
                src='/images/checkout-receipt.webp'
                alt='Customer showing a receipt QR code to a cashier at checkout'
              />
              <div className='home-saved-badge'>
                Digital receipts. No paper.
              </div>
              <div className='home-preview-caption'>
                <span className='home-caption-line' />
                From checkout to your account in seconds
              </div>
            </div>
          </Col>
        </Row>
      </section>

      <section className='home-how-it-works' aria-labelledby='how-it-works'>
        <div className='home-section-heading'>
          <span className='home-section-kicker'>SIMPLE BY DESIGN</span>
          <h2 id='how-it-works'>A receipt, without the paper.</h2>
        </div>
        <Row className='g-4'>
          <Col md={4}>
            <div className='home-step'>
              <span className='home-step-number'>01</span>
              <h3>Make your account</h3>
              <p>Create a shopper account and get your own QR code.</p>
            </div>
          </Col>
          <Col md={4}>
            <div className='home-step'>
              <span className='home-step-number'>02</span>
              <h3>Scan at checkout</h3>
              <p>Show your code when you pay at a participating business.</p>
            </div>
          </Col>
          <Col md={4}>
            <div className='home-step'>
              <span className='home-step-number'>03</span>
              <h3>Find it when you need it</h3>
              <p>Your digital receipts are ready to search in your account.</p>
            </div>
          </Col>
        </Row>
      </section>

      <section
        className='home-audience'
        aria-label='Choose how to use MicroReceipt'
      >
        <Row className='g-4'>
          <Col md={6}>
            <div className='home-audience-card'>
              <div className='home-audience-icon'>
                <FontAwesomeIcon icon={faReceipt} />
              </div>
              <span className='home-section-kicker'>FOR SHOPPERS</span>
              <h2>Keep every receipt close.</h2>
              <p>
                Build a searchable record of your purchases and leave paper
                behind at participating checkouts.
              </p>
              <Button
                as={Link}
                to='/register'
                variant='outline-primary'
                className='home-text-link'
              >
                Create a shopper account <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          </Col>
          <Col md={6}>
            <div className='home-audience-card home-business-card'>
              <div className='home-audience-icon'>
                <FontAwesomeIcon icon={faQrcode} />
              </div>
              <span className='home-section-kicker'>FOR BUSINESSES</span>
              <h2>Make checkout work smarter.</h2>
              <p>
                Offer customers a contactless way to receive digital receipts
                and simplify the paper handoff.
              </p>
              <Button
                as={Link}
                to='/businessregister'
                variant='outline-primary'
                className='home-text-link'
              >
                Explore business registration{' '}
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          </Col>
        </Row>
      </section>

      <section className='home-purpose'>
        <div className='home-purpose-icon'>
          <FontAwesomeIcon icon={faLeaf} />
        </div>
        <div>
          <span className='home-section-kicker'>
            LESS PAPER, MORE POSSIBILITY
          </span>
          <h2>Small changes add up.</h2>
          <p>
            Digital receipts offer a convenient alternative to printed slips.
            MicroReceipt is built to make that switch easy for shoppers and the
            businesses they visit.
          </p>
        </div>
        <Button as={Link} to='/about' variant='outline-primary'>
          Our mission <FontAwesomeIcon icon={faArrowRight} />
        </Button>
      </section>
    </div>
  )
}

export default HomeScreen
